/**
 * App-level pull-to-refresh registration context.
 *
 * Pattern mirrors BulkRunContext: provider = state only (eager, tiny),
 * dock/consumer is separate. Each role's dashboard data hook calls
 * useRegisterPullRefresh(refreshFn) once; AppShell calls
 * usePullRefreshHandler() to wire the gesture.
 *
 * Latest-ref pattern: the registered fn is stored in a ref so its identity
 * changes every render without causing effect churn. Only a boolean
 * hasHandler tracks mount/unmount for AppShell's enabled gate.
 *
 * TWO-SLOT PRIORITY: there are two registration slots — the dashboard slot
 * (`handlerRef`, via `useRegisterPullRefresh`, one per role's dashboard data
 * hook) and the surface slot (`surfaceRef`, via `useRegisterSurfaceRefresh`,
 * filled by whichever tab is currently on screen). `refresh()` prefers the
 * surface slot and falls back to the dashboard slot — so pulling on a
 * non-dashboard tab refreshes what the user is actually looking at instead
 * of always reloading the role's dashboard underneath it.
 *
 * IDENTITY-GUARDED cleanup: both slots' unregister fns only null the ref
 * (and flip their `has*` flag) when the ref still holds the SAME fn the
 * cleanup closed over. Without this, an unmount race — a stale registrant's
 * cleanup running after a newer registrant has already taken the slot —
 * would null out the newer, live handler. `KeepAliveTab` keeps tabs mounted
 * (not unmounted) on switch, but effects can still re-run/re-order across
 * renders, so the guard is required even though it may look redundant.
 *
 * Degrades gracefully without a provider: hooks are no-ops/return null
 * (mirrors useLifecycleActivation degrade pattern).
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

const PullRefreshContext = createContext(null);

export function PullRefreshProvider({ children, signedIn }) {
  const [hasHandler, setHasHandler] = useState(false);
  // BREATH signal (mockup 21 follow-up): true while a MANUAL refresh runs
  // (sidebar re-click today; mobile PTR opt-in later). Content areas read it
  // to dim softly — data never unmounts, scroll never moves; purely opacity.
  const [manualRefreshing, setManualRefreshing] = useState(false);
  // Outcome of the last owning run: 'idle' | 'success' | 'failed'. Auto-resets
  // after the success hold so the sidebar icon returns to itself. Combined with
  // manualRefreshing it yields `refreshPhase` (see value below).
  const [refreshOutcome, setRefreshOutcome] = useState('idle');
  const outcomeTimerRef = useRef(null);
  const resetRefreshPhase = useCallback(() => {
    if (outcomeTimerRef.current) { clearTimeout(outcomeTimerRef.current); outcomeTimerRef.current = null; }
    setRefreshOutcome('idle');
    setManualRefreshing(false); // a tab switch ends any in-flight narration/dim
  }, []);
  const reportRefreshOutcome = useCallback((outcome) => {
    if (outcomeTimerRef.current) { clearTimeout(outcomeTimerRef.current); outcomeTimerRef.current = null; }
    if (outcome !== 'success' && outcome !== 'failed') { setRefreshOutcome('idle'); return; }
    setRefreshOutcome(outcome);
    outcomeTimerRef.current = setTimeout(() => {
      outcomeTimerRef.current = null;
      setRefreshOutcome('idle');
    }, 450);
  }, []);
  useEffect(() => () => { if (outcomeTimerRef.current) clearTimeout(outcomeTimerRef.current); }, []);
  // Mutable ref holds the latest registered fn — no subscription churn.
  const handlerRef = useRef(null);
  // Surface slot: the currently-visible tab's own refresh fn, when it has one.
  // Takes priority over handlerRef — see the two-slot priority note above.
  const surfaceRef = useRef(null);
  const [hasSurface, setHasSurface] = useState(false);

  // Exposed to child hooks via context. Gated on `signedIn`: a registrant that
  // stays mounted across a sign-out (see the security note below — today's
  // registrants unmount via the KeyedRoutesAndAi key remount, but a future one
  // mounted ABOVE that key would not) would otherwise just re-register itself
  // the instant its own effect re-runs off a `ctx` identity change, since
  // useRegisterPullRefresh/useRegisterSurfaceRefresh know nothing about auth
  // state. Refusing the register call itself (not just reacting after the
  // fact) is what actually stops that self-healing loop. `signedIn` is
  // undefined in isolated provider tests, so the `=== false` check leaves them
  // untouched (mirrors the effect below).
  const _register = useCallback((fn) => {
    if (signedIn === false) return () => {};
    handlerRef.current = fn;
    setHasHandler(true);
    return () => {
      // Identity guard: only clear if this cleanup's fn is still the live one —
      // a stale unmount must never null a newer registrant. See file header.
      if (handlerRef.current === fn) {
        handlerRef.current = null;
        setHasHandler(false);
      }
    };
  }, [signedIn]);

  const _registerSurface = useCallback((fn) => {
    if (signedIn === false) return () => {};
    surfaceRef.current = fn;
    setHasSurface(true);
    return () => {
      if (surfaceRef.current === fn) {
        surfaceRef.current = null;
        setHasSurface(false);
      }
    };
  }, [signedIn]);

  // Stable refresh fn — surface slot wins, dashboard slot is the fallback.
  const refresh = useCallback(() => {
    if (typeof surfaceRef.current === 'function') {
      return surfaceRef.current();
    }
    if (typeof handlerRef.current === 'function') {
      return handlerRef.current();
    }
    return Promise.resolve();
  }, []);

  // Security: mirrors BulkRunContext / LifecycleActivationContext. Nothing here
  // holds tenant DATA today (both slots are just refresh callback closures), but
  // this provider is mounted ABOVE the auth gate in App.js — a sign-out is a
  // client-side setSessionUser(null), not a reload, so any registrant left in
  // these refs would keep firing tenant fetches on a pull-to-refresh gesture
  // performed on the now-logged-out screen. Clear both slots the moment the
  // session ends so a stale (or future) registrant can't act post-sign-out, and
  // can't resurrect if the user signs back in on the same tab (no reload).
  // `signedIn` is undefined in isolated provider tests, so the `=== false`
  // guard leaves them untouched.
  useEffect(() => {
    if (signedIn !== false) return undefined;
    handlerRef.current = null;
    surfaceRef.current = null;
    setHasHandler(false);
    setHasSurface(false);
    return undefined;
  }, [signedIn]);

  // Memoized so the value identity only changes when hasHandler/hasSurface flips
  // (a real mount/unmount of a refresh handler) or signedIn changes (session
  // start/end — see _register/_registerSurface above). refresh is always
  // useCallback-stable. Previously the inline object made useRegisterPullRefresh
  // (deps [ctx]) re-register/cleanup on every provider re-render.
  const value = useMemo(
    () => ({
      hasHandler,
      hasSurface,
      refresh,
      _register,
      _registerSurface,
      manualRefreshing,
      setManualRefreshing,
      // 'idle' | 'refreshing' | 'success' | 'failed' — ONE shared state for the
      // sidebar icon narration (spinner while refreshing, check on success).
      refreshPhase: manualRefreshing ? 'refreshing' : refreshOutcome,
      reportRefreshOutcome,
      resetRefreshPhase,
    }),
    [hasHandler, hasSurface, refresh, _register, _registerSurface, manualRefreshing,
      refreshOutcome, reportRefreshOutcome, resetRefreshPhase],
  );

  return (
    <PullRefreshContext.Provider value={value}>
      {children}
    </PullRefreshContext.Provider>
  );
}

PullRefreshProvider.propTypes = { children: PropTypes.node, signedIn: PropTypes.bool };

/**
 * Register this role's refresh function with the PTR system.
 * Call once per dashboard data hook. The fn ref is updated each render
 * without re-subscribing (latest-ref pattern).
 *
 * No-ops gracefully when called outside a PullRefreshProvider.
 *
 * @param {() => Promise<void>|void} fn  Latest refresh callback.
 */
export function useRegisterPullRefresh(fn) {
  const ctx = useContext(PullRefreshContext);
  // Keep the ref current on every render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!ctx) return undefined;
    // Register a stable wrapper that always calls the latest fn.
    const cleanup = ctx._register(() => fnRef.current?.());
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);
}

/**
 * Register the CURRENTLY VISIBLE surface's own refresh fn. Takes priority over
 * the role dashboard handler while `active` is true. Latest-ref pattern so the
 * fn identity may change every render without effect churn.
 *
 * `active` MUST be false when the surface is off-screen — KeepAliveTab keeps
 * inactive tabs mounted, so an always-true registration would hijack the pull
 * on every other tab for the rest of the session.
 *
 * No-ops gracefully when called outside a PullRefreshProvider.
 *
 * @param {() => Promise<void>|void} fn  Latest refresh callback.
 * @param {boolean} [active]  Whether this surface is the one on screen.
 */
export function useRegisterSurfaceRefresh(fn, active = true) {
  const ctx = useContext(PullRefreshContext);
  // Keep the ref current on every render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!ctx || !active) return undefined;
    const cleanup = ctx._registerSurface(() => fnRef.current?.());
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, active]);
}

/**
 * Read the pull-to-refresh handler state.
 * Used by AppShell to wire the gesture.
 *
 * Returns { hasHandler: false, refresh: null } when no provider is present.
 *
 * @returns {{ hasHandler: boolean, refresh: (() => Promise<void>)|null }}
 */
export function usePullRefreshHandler() {
  const ctx = useContext(PullRefreshContext);
  if (!ctx) {
    return {
      hasHandler: false, refresh: null, setManualRefreshing: null,
      refreshPhase: 'idle', reportRefreshOutcome: null, resetRefreshPhase: null,
    };
  }
  return {
    hasHandler: ctx.hasHandler || ctx.hasSurface,
    refresh: ctx.refresh,
    setManualRefreshing: ctx.setManualRefreshing,
    refreshPhase: ctx.refreshPhase,
    reportRefreshOutcome: ctx.reportRefreshOutcome,
    resetRefreshPhase: ctx.resetRefreshPhase,
  };
}

/**
 * Content-side reader for the manual-refresh BREATH: true while a manual
 * refresh (sidebar re-click / opted-in gestures) is running. No-ops to false
 * outside a provider.
 */
export function useManualRefreshActive() {
  const ctx = useContext(PullRefreshContext);
  return ctx ? ctx.manualRefreshing : false;
}
